import { useEffect } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  GetSkipCategoryPreferencesQuery,
  GetSkipCategoryPreferencesQueryVariables,
  UpdateSkipCategoryPreferencesMutation,
  UpdateSkipCategoryPreferencesMutationVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import {
  GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
  UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER,
} from './ParentalControlsService'
import { countLeaves, countSkippedLeaves } from './preference-tree'

export type SkipCategoryPreferenceItem = NonNullable<
  NonNullable<NonNullable<GetSkipCategoryPreferencesQuery['user']>['parentalControlPreferences']>[number]
>

type UseSkipCategoryPreferencesParams = {
  lang: string
  contentId?: string
}

export const useSkipCategoryPreferences = ({ lang, contentId }: UseSkipCategoryPreferencesParams) => {
  const { cache } = useApolloClient()
  const { data, loading, error, refetch } = useQuery<
    GetSkipCategoryPreferencesQuery,
    GetSkipCategoryPreferencesQueryVariables
  >(GET_SKIP_CATEGORY_PREFERENCES_FOR_USER, {
    variables: { language: lang, ...(contentId ? { id: contentId } : {}) },
    context: { requireAuth: true },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) {
      const expectedMessages = ['language not supported', 'not_found']
      const isExpectedError = expectedMessages.some((msg) => error.message?.toLowerCase().includes(msg))
      if (!isExpectedError) {
        logger().error('Failed to fetch skip category preferences', { lang }, error)
      }
    }
  }, [error, lang])

  const [executeUpdate, { loading: updating }] = useMutation<
    UpdateSkipCategoryPreferencesMutation,
    UpdateSkipCategoryPreferencesMutationVariables
  >(UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER)
  const rawPreferences = (data?.user?.parentalControlPreferences ?? []).filter(isDefined)

  const parents = rawPreferences.filter((p) => p.parentId == null)
  const children = rawPreferences.filter((p) => p.parentId != null)
  const orderedPreferences = parents.flatMap((parent) => {
    const parentChildren = children.filter((child) => child.parentId === parent.id)
    return [parent, ...parentChildren]
  })

  const queryVariables = { language: lang, ...(contentId ? { id: contentId } : {}) }

  const updateSkipPreference = async (categoryId: number, skipped: boolean) => {
    // Read current cache to get the latest state (not stale closure from render)
    const cachedData = cache.readQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
      query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
      variables: queryVariables,
    })
    const existingPrefs = (cachedData?.user?.parentalControlPreferences ?? []).filter(isDefined)
    const userId = cachedData?.user?.id ?? ''
    const needsReview = cachedData?.user?.needsReview ?? true
    const targetPref = existingPrefs.find((p) => p.id === categoryId)
    const isParent = existingPrefs.some((p) => p.parentId === categoryId)

    // Build optimistic preferences: update the target and its descendants
    const idsToUpdate = new Set<number>([categoryId])
    if (isParent) {
      for (const p of existingPrefs) {
        if (p.parentId === categoryId) idsToUpdate.add(p.id)
      }
    }

    const optimisticPrefs = existingPrefs.map((p) => {
      if (!idsToUpdate.has(p.id)) {
        // Update parent skippedCount if this is a parent of the toggled item
        if (p.id === targetPref?.parentId) {
          const childrenOfParent = existingPrefs.filter((c) => c.parentId === p.id)
          const newSkippedCount = childrenOfParent.reduce((count, c) => {
            return count + (idsToUpdate.has(c.id) ? (skipped ? 1 : 0) : c.skipped ? 1 : 0)
          }, 0)
          return { ...p, skippedCount: newSkippedCount }
        }
        return p
      }
      const newSkippedCount = isParent && p.id === categoryId ? (skipped ? p.totalCount : 0) : p.skippedCount
      return { ...p, skipped, skippedCount: newSkippedCount }
    })

    // Optimistically write cache immediately
    cache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
      query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
      variables: queryVariables,
      data: {
        user: {
          __typename: 'User',
          id: userId,
          needsReview,
          parentalControlPreferences: optimisticPrefs,
        },
      },
    })

    try {
      await executeUpdate({
        variables: { language: lang, updates: [{ categoryId, skipped }], ...(contentId ? { contentId } : {}) },
        context: { requireAuth: true },
        update(updateCache, { data: mutationData }) {
          const updatedPreferences = mutationData?.updateParentalControlPreferences
          if (!updatedPreferences) return

          const currentData = updateCache.readQuery<
            GetSkipCategoryPreferencesQuery,
            GetSkipCategoryPreferencesQueryVariables
          >({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
          })

          const currentPrefs = currentData?.user?.parentalControlPreferences ?? []
          const updatedMap = new Map(updatedPreferences.filter(isDefined).map((p) => [p.id, p]))

          const mergedPreferences = currentPrefs.map((existing) => {
            if (!existing) return existing
            const updated = updatedMap.get(existing.id)
            if (!updated) return existing
            return { ...existing, ...updated, isNew: existing.isNew }
          })

          updateCache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
            data: {
              user: {
                __typename: 'User',
                id: userId,
                needsReview,
                parentalControlPreferences: mergedPreferences,
              },
            },
          })
        },
      })
    } catch (err) {
      // Revert only the failed preference(s), preserving other in-flight optimistic updates.
      // We build a map of pre-mutation values for the IDs this call touched,
      // then read the *current* cache (which may include other optimistic writes) and
      // surgically restore only the affected entries.
      const preUpdateMap = new Map(
        existingPrefs.filter((p) => idsToUpdate.has(p.id) || p.id === targetPref?.parentId).map((p) => [p.id, p]),
      )

      const currentData = cache.readQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
        query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
        variables: queryVariables,
      })
      const currentPrefs = (currentData?.user?.parentalControlPreferences ?? []).filter(isDefined)

      const revertedPrefs = currentPrefs.map((p) => {
        const original = preUpdateMap.get(p.id)
        return original ?? p
      })

      cache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
        query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
        variables: queryVariables,
        data: {
          user: {
            __typename: 'User',
            id: userId,
            needsReview,
            parentalControlPreferences: revertedPrefs,
          },
        },
      })
      logger().error('Failed to update skip category preference', { lang, categoryId, skipped }, err)
    }
  }

  const totalCount = countLeaves(rawPreferences)
  const totalSkippedCount = countSkippedLeaves(rawPreferences)
  const hasAppliedControls = totalSkippedCount > 0

  return {
    loading,
    updating,
    error,
    preferences: orderedPreferences,
    allPreferences: rawPreferences,
    updateSkipPreference,
    refetch,
    totalSkippedCount,
    totalCount,
    hasAppliedControls,
  }
}
